import type { ReactNode } from 'react';

import type { InboxGroup } from './use-feedback-inbox.js';

// ---------------------------------------------------------------------------
// Shared poll-activity helpers.
//
// The activity panel and the message feed both surface poll-related
// rows (created / voted / closed) with the same label band on top and
// the same "Voted: A, B" detail under the poll pill. These helpers
// keep the two render paths in lockstep so a wording or formatting
// change lands in both places at once.
// ---------------------------------------------------------------------------

// Did this group's lastActivityAt come from a poll-specific event?
// 'created' covers a brand-new poll-bearing message with no votes
// yet; 'vote' a vote add/remove; 'closed' a finalisation flip.
// Returns null when the activity is generic (reaction, reply, etc).
export function pollEventKind(
  group: InboxGroup,
): 'created' | 'vote' | 'closed' | null {
  // Cast-shaped test fixtures may omit pollStatus; treat anything
  // not strictly truthy as no poll.
  const status = group.head.pollStatus;
  if (status === null || status === undefined) return null;
  if (status.lastEventAt !== group.lastActivityAt) return null;
  if (status.finalised) return 'closed';
  if (status.lastVote === null) return 'created';
  return 'vote';
}

// "voted in #channel" / "created poll in #channel" / "poll closed in #channel".
// For threads, surfaces the parent channel as the secondary scope.
export function pollSourceLabel(group: InboxGroup): string | null {
  const kind = pollEventKind(group);
  if (kind === null) return null;
  const head = group.head;
  const channel = head.discordChannelName ?? '(channel)';
  const verb =
    kind === 'closed'
      ? 'poll closed'
      : kind === 'created'
        ? 'created poll'
        : 'voted';
  if (head.discordParentChannelName !== null) {
    return `${verb} in ${channel} · #${head.discordParentChannelName}`;
  }
  return `${verb} in #${channel}`;
}

// Renders the "Voted: A, B, C" line shown under the poll pill on
// vote-driven activity tiles. Returns null when there's nothing to
// show (no lastVote, or this isn't a vote event).
export function pollVoteDetailFor(group: InboxGroup): ReactNode | null {
  if (pollEventKind(group) !== 'vote') return null;
  const lastVote = group.head.pollStatus?.lastVote ?? null;
  if (lastVote === null) return null;
  if (lastVote.choices.length === 0) {
    return <span className="italic">vote removed</span>;
  }
  const labels = lastVote.choices.map((c) => {
    const text = c.text?.trim();
    const emoji =
      c.emoji !== null && c.emoji.id === null && c.emoji.name !== null
        ? c.emoji.name
        : null;
    if (text !== undefined && text.length > 0) {
      return emoji === null ? text : `${emoji} ${text}`;
    }
    return emoji ?? `#${c.answerId}`;
  });
  return <span>Voted: {labels.join(', ')}</span>;
}
